import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ACTIONS, ActionKey, ICON_SIZE, IconComponent } from './actions';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';

// Literais de propósito: o Tailwind descarta classes de @layer components montadas por template.
const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  outline: 'btn-outline',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
};

interface BaseProps {
  variant?: ButtonVariant;
  /** 'sm' para cabeçalhos de lista/barras de ferramenta. Mobile mantém 44px de toque. */
  size?: 'sm' | 'md';
  /** Ícone à esquerda: componente lucide ou chave do registro de ações (ícone consistente). */
  icon?: IconComponent | ActionKey;
  iconRight?: IconComponent;
  loading?: boolean;
  fullWidth?: boolean;
  className?: string;
  children?: React.ReactNode;
}

type ButtonAsButton = BaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & { to?: undefined; href?: undefined };
type ButtonAsLink = BaseProps & { to: string; href?: undefined; onClick?: React.MouseEventHandler; target?: string };
type ButtonAsAnchor = BaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & { href: string; to?: undefined };

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

/** Botão padrão do sistema (sobre .btn + .btn-<variant> do index.css). */
const Button: React.FC<ButtonProps> = (props) => {
  const {
    variant = 'primary',
    size = 'md',
    icon,
    iconRight: IconRight,
    loading,
    fullWidth,
    className,
    children,
    ...rest
  } = props;

  const LeftIcon = loading ? Loader2 : typeof icon === 'string' ? ACTIONS[icon].icon : icon;
  const classes = cx(
    'btn',
    VARIANT_CLASS[variant],
    size === 'sm' && 'btn--sm',
    fullWidth && 'w-full',
    className
  );

  const content = (
    <>
      {LeftIcon && <LeftIcon className={cx(ICON_SIZE.button, loading && 'animate-spin')} aria-hidden="true" />}
      {children}
      {IconRight && <IconRight className={ICON_SIZE.button} aria-hidden="true" />}
    </>
  );

  if ('to' in rest && rest.to) {
    const { to, ...linkRest } = rest as ButtonAsLink;
    return (
      <Link to={to} className={classes} {...(linkRest as object)}>
        {content}
      </Link>
    );
  }

  if ('href' in rest && rest.href) {
    return (
      <a className={classes} {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {content}
      </a>
    );
  }

  const buttonRest = rest as React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button
      type="button"
      {...buttonRest}
      className={classes}
      disabled={buttonRest.disabled || loading}
      aria-busy={loading || undefined}
    >
      {content}
    </button>
  );
};

export default Button;
